import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, ToggleButton, ToggleButtonGroup, TextField, InputAdornment, IconButton,
  Card, CardContent, Chip, CircularProgress, Alert, Button, Tooltip, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Paper, Grid
} from '@mui/material';
import { ChevronLeft, ChevronRight, Search, Clear, FiberNew, HourglassBottom, Restaurant } from '@mui/icons-material';
import api from '../api';
import { MONATE } from '../konstanten';

export interface SaisonEintrag {
  name: string;
  gruppe: 'Gemüse' | 'Obst' | 'Fisch';
  saison: number[];
  lager?: number[];
  ganzjaehrig?: boolean;
  herkunft?: string;
  hinweis?: string;
  suchbegriffe: string[];
  anzahlGerichte: number;
}

type Status = 'saison' | 'lager' | 'ganzjaehrig' | 'ausserhalb';

const GRUPPEN = ['Gemüse', 'Obst', 'Fisch'] as const;
const GRUPPEN_FARBEN: Record<string, string> = { Gemüse: '#388e3c', Obst: '#c2185b', Fisch: '#0277bd' };
const STATUS_FARBEN: Record<Status, string> = {
  saison: '#66bb6a', lager: '#d7c4a3', ganzjaehrig: '#e3e3e3', ausserhalb: 'transparent',
};
const STATUS_TEXT: Record<Status, string> = {
  saison: 'Saison', lager: 'Lagerware', ganzjaehrig: 'ganzjährig (Zucht/Gewächshaus)', ausserhalb: '',
};

const vorher = (m: number) => (m === 1 ? 12 : m - 1);
const nachher = (m: number) => (m === 12 ? 1 : m + 1);

export function status(e: SaisonEintrag, monat: number): Status {
  if (e.saison.includes(monat)) return 'saison';
  if (e.lager?.includes(monat)) return 'lager';
  if (e.ganzjaehrig) return 'ganzjaehrig';
  return 'ausserhalb';
}

function EintragKarte({ e, monat, onGerichte }: { e: SaisonEintrag; monat: number; onGerichte?: (b: string) => void }) {
  const st = status(e, monat);
  const neu = st === 'saison' && !e.saison.includes(vorher(monat));
  const endet = st === 'saison' && !e.saison.includes(nachher(monat));
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, borderRadius: 1,
      '&:hover': { bgcolor: 'action.hover' },
    }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: STATUS_FARBEN[st], border: st === 'ganzjaehrig' ? '1px solid #bbb' : 'none' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {e.name}
          {e.herkunft && <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>({e.herkunft})</Typography>}
        </Typography>
        {e.hinweis && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>{e.hinweis}</Typography>}
      </Box>
      {neu && <Tooltip title={`Neu seit ${MONATE[monat - 1]}`}><FiberNew fontSize="small" sx={{ color: '#2e7d32' }} /></Tooltip>}
      {endet && <Tooltip title={`Letzter Saisonmonat`}><HourglassBottom fontSize="small" sx={{ color: '#ef6c00' }} /></Tooltip>}
      {e.anzahlGerichte > 0 && (
        <Tooltip title={onGerichte ? `Gerichte mit ${e.name} anzeigen` : `${e.anzahlGerichte} Gerichte mit ${e.name}`}>
          <Chip size="small" icon={<Restaurant sx={{ fontSize: '0.9rem !important' }} />} label={e.anzahlGerichte} variant="outlined"
            onClick={onGerichte ? () => onGerichte(e.suchbegriffe[0]) : undefined} sx={{ cursor: onGerichte ? 'pointer' : 'default' }} />
        </Tooltip>
      )}
    </Box>
  );
}

function MonatsAnsicht({ eintraege, monat, onGerichte }: { eintraege: SaisonEintrag[]; monat: number; onGerichte?: (b: string) => void }) {
  const neuListe = eintraege.filter(e => e.saison.includes(monat) && !e.saison.includes(vorher(monat)));
  const endetListe = eintraege.filter(e => e.saison.includes(monat) && !e.saison.includes(nachher(monat)));
  const gruppen = GRUPPEN.filter(g => eintraege.some(e => e.gruppe === g));
  const reihenfolge: Status[] = ['saison', 'lager', 'ganzjaehrig'];

  return (
    <>
      {(neuListe.length > 0 || endetListe.length > 0) && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          {neuListe.length > 0 && <div><strong>Neu im {MONATE[monat - 1]}:</strong> {neuListe.map(e => e.name).join(', ')}</div>}
          {endetListe.length > 0 && <div><strong>Letzte Gelegenheit:</strong> {endetListe.map(e => e.name).join(', ')}</div>}
        </Alert>
      )}
      <Grid container spacing={2}>
        {gruppen.map(g => {
          const liste = eintraege
            .filter(e => e.gruppe === g && status(e, monat) !== 'ausserhalb')
            .sort((a, b) => reihenfolge.indexOf(status(a, monat)) - reihenfolge.indexOf(status(b, monat)) || a.name.localeCompare(b.name, 'de'));
          return (
            <Grid item xs={12} md={gruppen.length === 1 ? 12 : 4} key={g}>
              <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${GRUPPEN_FARBEN[g]}` }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {g} <Typography component="span" sx={{ color: 'text.secondary' }}>({liste.length})</Typography>
                  </Typography>
                  {liste.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nichts in Saison.</Typography>}
                  {reihenfolge.map(st => {
                    const teil = liste.filter(e => status(e, monat) === st);
                    if (!teil.length) return null;
                    return (
                      <Box key={st} sx={{ mb: 1.5 }}>
                        {st !== 'saison' && (
                          <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                            {st === 'lager' ? 'Aus dem Lager' : 'Ganzjährig verfügbar'}
                          </Typography>
                        )}
                        {teil.map(e => <EintragKarte key={e.name} e={e} monat={monat} onGerichte={onGerichte} />)}
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}

function JahresAnsicht({ eintraege, monat, onGerichte }: { eintraege: SaisonEintrag[]; monat: number; onGerichte?: (b: string) => void }) {
  const gruppen = GRUPPEN.filter(g => eintraege.some(e => e.gruppe === g));
  const zelle = { px: 0, py: 0.5, width: 36, minWidth: 36, textAlign: 'center' as const };
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 260px)' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper', minWidth: 170 }}>Zutat</TableCell>
            {MONATE.map((m, i) => (
              <TableCell key={m} sx={{ ...zelle, fontWeight: i + 1 === monat ? 700 : 400, color: i + 1 === monat ? 'primary.main' : undefined, bgcolor: i + 1 === monat ? '#e8f0fb' : undefined }}>
                {m.slice(0, 3)}
              </TableCell>
            ))}
            <TableCell sx={{ minWidth: 70 }}>Gerichte</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {gruppen.map(g => (
            <React.Fragment key={g}>
              <TableRow>
                <TableCell colSpan={14} sx={{ bgcolor: '#fafafa', fontWeight: 700, color: GRUPPEN_FARBEN[g], position: 'sticky', left: 0 }}>{g}</TableCell>
              </TableRow>
              {eintraege.filter(e => e.gruppe === g).map(e => (
                <TableRow key={e.name} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>
                    <Tooltip title={e.hinweis || ''} placement="right">
                      <span>{e.name}{e.herkunft ? ` (${e.herkunft})` : ''}</span>
                    </Tooltip>
                  </TableCell>
                  {MONATE.map((m, i) => {
                    const st = status(e, i + 1);
                    return (
                      <TableCell key={m} sx={i + 1 === monat ? { ...zelle, bgcolor: 'rgba(25, 118, 210, 0.06)' } : zelle}>
                        <Tooltip title={st === 'ausserhalb' ? '' : `${e.name}, ${m}: ${STATUS_TEXT[st]}`}>
                          <Box sx={{ height: 18, mx: '2px', borderRadius: 0.5, bgcolor: STATUS_FARBEN[st] }} />
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    {e.anzahlGerichte > 0 && (onGerichte
                      ? <Button size="small" sx={{ minWidth: 0 }} onClick={() => onGerichte(e.suchbegriffe[0])}>{e.anzahlGerichte}</Button>
                      : <Typography variant="body2" sx={{ color: 'text.secondary' }}>{e.anzahlGerichte}</Typography>)}
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Legende() {
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', color: 'text.secondary', fontSize: '0.8rem', alignItems: 'center' }}>
      {(['saison', 'lager', 'ganzjaehrig'] as Status[]).map(st => (
        <Box key={st} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: STATUS_FARBEN[st], border: st === 'ganzjaehrig' ? '1px solid #bbb' : 'none' }} />
          {STATUS_TEXT[st]}
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><FiberNew fontSize="small" sx={{ color: '#2e7d32' }} />neu in Saison</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><HourglassBottom fontSize="small" sx={{ color: '#ef6c00' }} />letzter Monat</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Restaurant sx={{ fontSize: '1rem' }} />Anzahl Gerichte in der Datenbank</Box>
    </Box>
  );
}

// Ohne onGerichteZeigen (Rolle Standard) gibt es keinen Sprung zu den Gerichten
export default function Saisonkalender({ onGerichteZeigen }: { onGerichteZeigen?: (suche: string) => void }) {
  const [eintraege, setEintraege] = useState<SaisonEintrag[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [monat, setMonat] = useState(new Date().getMonth() + 1);
  const [ansicht, setAnsicht] = useState<'monat' | 'jahr'>('monat');
  const [gruppe, setGruppe] = useState<string>('Alle');
  const [suche, setSuche] = useState('');

  const laden_ = () => {
    setLaden(true);
    setFehler('');
    api.get('/saisonkalender')
      .then(res => setEintraege(res.data))
      .catch(() => setFehler('Saisonkalender konnte nicht geladen werden.'))
      .finally(() => setLaden(false));
  };
  useEffect(laden_, []);

  const gefiltert = useMemo(() => {
    const s = suche.trim().toLowerCase();
    return eintraege
      .filter(e => gruppe === 'Alle' || e.gruppe === gruppe)
      .filter(e => !s || e.name.toLowerCase().includes(s) || e.suchbegriffe.some(b => b.includes(s)))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [eintraege, gruppe, suche]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Saisonkalender</Typography>
        <ToggleButtonGroup size="small" exclusive value={ansicht} onChange={(_, v) => v && setAnsicht(v)}>
          <ToggleButton value="monat">Monat</ToggleButton>
          <ToggleButton value="jahr">Jahresübersicht</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        Was hat wann regional Saison? Richtwerte für Süddeutschland, je nach Witterung ±2 Wochen.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton aria-label="Vorheriger Monat" onClick={() => setMonat(vorher)}><ChevronLeft /></IconButton>
          <Typography sx={{ fontWeight: 600, minWidth: 100, textAlign: 'center' }}>{MONATE[monat - 1]}</Typography>
          <IconButton aria-label="Nächster Monat" onClick={() => setMonat(nachher)}><ChevronRight /></IconButton>
          {monat !== new Date().getMonth() + 1 && (
            <Button size="small" onClick={() => setMonat(new Date().getMonth() + 1)}>Heute</Button>
          )}
        </Box>
        <ToggleButtonGroup size="small" exclusive value={gruppe} onChange={(_, v) => v && setGruppe(v)}>
          {['Alle', ...GRUPPEN].map(g => <ToggleButton key={g} value={g}>{g}</ToggleButton>)}
        </ToggleButtonGroup>
        <TextField
          size="small" label="Zutat suchen" value={suche} onChange={e => setSuche(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            endAdornment: suche ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Suche leeren" onClick={() => setSuche('')}><Clear fontSize="small" /></IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>
      <Box sx={{ mb: 2 }}><Legende /></Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={laden_}>Erneut laden</Button>}>{fehler}</Alert>}
      {laden ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : !fehler && gefiltert.length === 0 ? (
        <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 6 }}>Keine passende Zutat gefunden.</Typography>
      ) : !fehler && (
        ansicht === 'monat'
          ? <MonatsAnsicht eintraege={gefiltert} monat={monat} onGerichte={onGerichteZeigen} />
          : <JahresAnsicht eintraege={gefiltert} monat={monat} onGerichte={onGerichteZeigen} />
      )}
    </Box>
  );
}
